import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Input,
  Select,
  Table,
  Tag,
  Typography,
  Statistic,
  Space,
  Button,
  Tooltip,
  Modal,
  Form,
  Switch,
  Badge,
  message,
  Popconfirm,
  Radio,
  Divider,
  Alert,
  QRCode,
  ColorPicker
} from 'antd';
import {
  QrcodeOutlined,
  PlusOutlined,
  DownloadOutlined,
  CopyOutlined,
  EditOutlined,
  DeleteOutlined,
  SettingOutlined,
  AppleOutlined,
  AndroidOutlined,
  GlobalOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  EyeOutlined,
  RiseOutlined,
  LinkOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/fr';
import qrCampaignService, {
  QrCampaign,
  QrGlobalStats,
  StoreSettings
} from '../services/qrCampaignService';

dayjs.extend(relativeTime);
dayjs.locale('fr');

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const COLOR_PRESETS = [
  { label: 'Vert Forêt (Officiel)', value: '#0a543d' },
  { label: 'Orange Énergie', value: '#e65100' },
  { label: 'Bleu Royal', value: '#1677ff' },
  { label: 'Noir Intense', value: '#111827' }
];

const SOURCE_OPTIONS = [
  { label: '📄 Flyer / Prospectus', value: 'flyer' },
  { label: '🏢 Affiche Résidence / Immeuble', value: 'poster' },
  { label: '🪪 Carte de Visite Technicien', value: 'technician_card' },
  { label: '❄️ Autocollant sur Climatiseur', value: 'split_sticker' },
  { label: '🤝 Partenaire Immobilier / Commerce', value: 'partner' },
  { label: '📱 Réseaux Sociaux / Digital', value: 'social' },
  { label: '🏷️ Autre', value: 'other' }
];

const QUICK_LINK_PRESETS = [
  { label: '🌐 Site Web', url: 'https://smartmaintenance.ci' },
  { label: '💬 WhatsApp Support', url: 'https://wa.me/2250700000000' },
  { label: '🏷️ Nos Offres', url: 'https://smartmaintenance.ci/offres' },
  { label: '📄 Demande Devis', url: 'https://smartmaintenance.ci/devis' }
];

const QRCodesPage: React.FC = () => {
  const [campaigns, setCampaigns] = useState<QrCampaign[]>([]);
  const [stats, setStats] = useState<QrGlobalStats | null>(null);
  const [storeSettings, setStoreSettings] = useState<StoreSettings>({
    iosUrl: '',
    androidUrl: '',
    webUrl: 'https://smartmaintenance.ci'
  });
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);

  // Filtres
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState<string | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 15, total: 0 });

  // Modales
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<QrCampaign | null>(null);
  const [downloadingFormat, setDownloadingFormat] = useState<'png' | 'svg' | null>(null);

  // Générateur QR Express depuis un Lien
  const [isQuickQrModalOpen, setIsQuickQrModalOpen] = useState(false);
  const [customUrl, setCustomUrl] = useState('https://smartmaintenance.ci');
  const [customTitle, setCustomTitle] = useState('');
  const [customColor, setCustomColor] = useState('#0a543d');
  const [hasCustomLogo, setHasCustomLogo] = useState(true);
  const [customLevel, setCustomLevel] = useState<'L' | 'M' | 'Q' | 'H'>('H');
  const [redirectionType, setRedirectionType] = useState<'smart' | 'direct'>('smart');
  const [directLinkValue, setDirectLinkValue] = useState('');

  const [campaignForm] = Form.useForm();
  const [settingsForm] = Form.useForm();

  // Chargement des données
  const fetchCampaigns = useCallback(async (page = 1, searchVal = '', sourceVal?: string, statusVal?: string) => {
    setLoading(true);
    try {
      const res = await qrCampaignService.getCampaigns({
        page,
        limit: 15,
        search: searchVal || undefined,
        source: sourceVal || undefined,
        is_active: statusVal !== undefined && statusVal !== '' ? statusVal === 'active' : undefined
      });
      setCampaigns(res.data);
      setPagination(prev => ({
        ...prev,
        current: res.pagination.page,
        total: res.pagination.total
      }));
    } catch (err: any) {
      console.error('Erreur chargement campagnes QR:', err);
      message.error('Impossible de charger les campagnes QR');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const data = await qrCampaignService.getGlobalStats();
      setStats(data);
    } catch (err) {
      console.error('Erreur chargement stats QR:', err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const fetchStoreSettings = useCallback(async () => {
    setSettingsLoading(true);
    try {
      const data = await qrCampaignService.getStoreSettings();
      setStoreSettings(data);
      settingsForm.setFieldsValue(data);
    } catch (err) {
      console.error('Erreur chargement paramètres stores:', err);
    } finally {
      setSettingsLoading(false);
    }
  }, [settingsForm]);

  useEffect(() => {
    fetchCampaigns(1, search, sourceFilter, statusFilter);
    fetchStats();
    fetchStoreSettings();
  }, [fetchCampaigns, fetchStats, fetchStoreSettings]);

  // Actions
  const handleSearch = (value: string) => {
    setSearch(value);
    fetchCampaigns(1, value, sourceFilter, statusFilter);
  };

  const handleSourceChange = (val?: string) => {
    setSourceFilter(val);
    fetchCampaigns(1, search, val, statusFilter);
  };

  const handleStatusChange = (val?: string) => {
    setStatusFilter(val);
    fetchCampaigns(1, search, sourceFilter, val);
  };

  const copyToClipboard = (text: string, label = 'Lien') => {
    navigator.clipboard.writeText(text);
    message.success(`${label} copié dans le presse-papiers !`);
  };

  // Sauvegarder la configuration des stores
  const handleSaveStoreSettings = async () => {
    try {
      const values = await settingsForm.validateFields();
      setSettingsLoading(true);
      const updated = await qrCampaignService.updateStoreSettings(values);
      setStoreSettings(updated);
      message.success('✅ Liens des stores et de redirection enregistrés avec succès !');
      setIsSettingsModalOpen(false);
    } catch (err: any) {
      console.error('Erreur sauvegarde stores:', err);
      message.error(err.message || 'Erreur lors de l\'enregistrement des paramètres');
    } finally {
      setSettingsLoading(false);
    }
  };

  // Ouvrir modale de création
  const openCreateModal = () => {
    campaignForm.resetFields();
    setRedirectionType('smart');
    setDirectLinkValue('');
    campaignForm.setFieldsValue({
      primary_color: '#0a543d',
      has_logo: true,
      source: 'flyer',
      is_active: true,
      direct_url: '',
      ios_url: '',
      android_url: '',
      web_url: ''
    });
    setIsCreateModalOpen(true);
  };

  // Ouvrir modale d'édition
  const openEditModal = (camp: QrCampaign) => {
    setSelectedCampaign(camp);
    
    // Détection si la campagne a été configurée comme un lien direct unique :
    // 1) ios_url, android_url et web_url sont identiques et non vides
    // 2) ou seul web_url est renseigné (sans liens stores distincts)
    // 3) ou la source est 'social' / pas de liens stores spécifiques différents
    const directCandidate = camp.web_url || camp.ios_url || camp.android_url || '';
    const hasDifferentStoreLinks = !!(camp.ios_url && camp.android_url && camp.ios_url !== camp.android_url);
    const isDirect = !!directCandidate && (
      (camp.ios_url === camp.web_url && camp.android_url === camp.web_url) ||
      (!camp.ios_url && !camp.android_url && !!camp.web_url) ||
      !hasDifferentStoreLinks
    );

    setRedirectionType(isDirect ? 'direct' : 'smart');
    setDirectLinkValue(isDirect ? directCandidate : '');

    campaignForm.resetFields();
    campaignForm.setFieldsValue({
      name: camp.name,
      code: camp.code,
      source: camp.source,
      location: camp.location,
      description: camp.description,
      primary_color: camp.primary_color || '#0a543d',
      has_logo: camp.has_logo !== false,
      direct_url: isDirect ? directCandidate : '',
      ios_url: isDirect ? '' : (camp.ios_url || ''),
      android_url: isDirect ? '' : (camp.android_url || ''),
      web_url: isDirect ? '' : (camp.web_url || ''),
      is_active: camp.is_active
    });
    setIsEditModalOpen(true);
  };

  // Ouvrir le générateur QR express depuis un lien
  const openQuickQrModal = () => {
    setIsQuickQrModalOpen(true);
  };

  // Télécharger PNG HD (1024px) du QR personnalisé généré
  const downloadCustomPng = () => {
    if (!customUrl.trim()) {
      message.warning('Veuillez renseigner un lien ou une URL à encoder');
      return;
    }
    const canvas = document.getElementById('custom-quick-qrcode')?.querySelector<HTMLCanvasElement>('canvas');
    if (!canvas) {
      message.error('QR Code introuvable pour le téléchargement');
      return;
    }

    try {
      const hdCanvas = document.createElement('canvas');
      hdCanvas.width = 1024;
      hdCanvas.height = 1024;
      const ctx = hdCanvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, 1024, 1024);
        ctx.imageSmoothingEnabled = false;
        const margin = 72;
        ctx.drawImage(canvas, margin, margin, 1024 - margin * 2, 1024 - margin * 2);

        const url = hdCanvas.toDataURL('image/png');
        const a = document.createElement('a');
        const cleanSlug = customTitle
          ? customTitle.toLowerCase().replace(/[^a-z0-9]/gi, '-').slice(0, 30)
          : 'custom-link';
        a.download = `smart-qr-${cleanSlug}.png`;
        a.href = url;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        message.success('✅ QR Code PNG HD (1024px) téléchargé avec succès !');
      }
    } catch (err) {
      console.error('Erreur export PNG:', err);
      message.error('Impossible d\'exporter le QR Code en PNG');
    }
  };

  // Télécharger SVG vectoriel du QR personnalisé généré
  const downloadCustomSvg = () => {
    if (!customUrl.trim()) {
      message.warning('Veuillez renseigner un lien ou une URL à encoder');
      return;
    }
    const svgEl = document.getElementById('custom-quick-qrcode-svg')?.querySelector<SVGSVGElement>('svg');
    if (!svgEl) {
      message.error('Erreur lors de la génération SVG');
      return;
    }
    try {
      const svgData = new XMLSerializer().serializeToString(svgEl);
      const blob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const cleanSlug = customTitle
        ? customTitle.toLowerCase().replace(/[^a-z0-9]/gi, '-').slice(0, 30)
        : 'custom-link';
      a.download = `smart-qr-${cleanSlug}.svg`;
      a.href = blobUrl;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
      message.success('✅ QR Code SVG vectoriel téléchargé avec succès !');
    } catch (err) {
      console.error('Erreur export SVG:', err);
      message.error('Impossible d\'exporter le QR Code en SVG');
    }
  };

  // Copier le lien personnalisé dans le presse-papiers
  const copyCustomUrl = () => {
    if (!customUrl.trim()) {
      message.warning('Veuillez renseigner un lien');
      return;
    }
    navigator.clipboard.writeText(customUrl.trim());
    message.success('Lien copié dans le presse-papiers !');
  };

  // Convertir le QR express en Campagne suivie avec statistiques en DB
  const convertCustomToCampaign = () => {
    if (!customUrl.trim()) {
      message.warning('Veuillez saisir un lien valide avant de créer la campagne');
      return;
    }
    const cleanUrl = customUrl.trim();
    setIsQuickQrModalOpen(false);
    openCreateModal();
    const suggestedName = customTitle.trim() || `Campagne ${cleanUrl.replace(/^https?:\/\//, '').split('/')[0] || 'Lien'}`;
    campaignForm.setFieldsValue({
      name: suggestedName,
      primary_color: customColor,
      has_logo: hasCustomLogo,
      direct_url: cleanUrl,
      web_url: cleanUrl,
      ios_url: cleanUrl,
      android_url: cleanUrl,
      source: 'social'
    });
    setRedirectionType('direct');
    setDirectLinkValue(cleanUrl);
    message.info('Lien reporté dans le formulaire de campagne. Vous pouvez finaliser et enregistrer.');
  };

  // Ouvrir modale d'aperçu & téléchargement HD
  const openPreviewModal = (camp: QrCampaign) => {
    setSelectedCampaign(camp);
    setIsPreviewModalOpen(true);
  };

  // Téléchargement robuste PNG/SVG avec logo incrusté
  const handleDownloadImage = async (format: 'png' | 'svg') => {
    if (!selectedCampaign) return;
    try {
      setDownloadingFormat(format);
      const url = qrCampaignService.getQrImageUrl(selectedCampaign.id, {
        format,
        download: true,
        width: 1024
      });
      const response = await fetch(url);
      if (!response.ok) throw new Error('Erreur lors du téléchargement');
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `qr-${selectedCampaign.code}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      message.success(`✅ QR Code ${format.toUpperCase()} téléchargé avec succès !`);
    } catch (err: any) {
      console.error('Erreur téléchargement QR:', err);
      message.error('Erreur lors du téléchargement du QR Code');
    } finally {
      setDownloadingFormat(null);
    }
  };

  // Soumission création
  const handleCreateCampaign = async () => {
    try {
      const values = await campaignForm.validateFields();
      
      const payload: any = { ...values };
      if (redirectionType === 'direct') {
        const directUrl = (values.direct_url || directLinkValue || '').trim();
        if (!directUrl) {
          message.warning('Veuillez renseigner le lien de destination directe');
          return;
        }
        payload.direct_url = directUrl;
        payload.web_url = directUrl;
        payload.ios_url = directUrl;
        payload.android_url = directUrl;
      } else {
        payload.direct_url = null;
        payload.ios_url = values.ios_url && values.ios_url.trim() !== '' ? values.ios_url.trim() : null;
        payload.android_url = values.android_url && values.android_url.trim() !== '' ? values.android_url.trim() : null;
        payload.web_url = values.web_url && values.web_url.trim() !== '' ? values.web_url.trim() : null;
      }

      setLoading(true);
      await qrCampaignService.createCampaign(payload);
      message.success('✅ Campagne QR Code créée avec succès !');
      setIsCreateModalOpen(false);
      fetchCampaigns(1, search, sourceFilter, statusFilter);
      fetchStats();
    } catch (err: any) {
      console.error('Erreur création:', err);
      message.error(err.response?.data?.message || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  // Soumission mise à jour
  const handleUpdateCampaign = async () => {
    if (!selectedCampaign) return;
    try {
      const values = await campaignForm.validateFields();
      
      const payload: any = { ...values };
      if (redirectionType === 'direct') {
        const directUrl = (values.direct_url || directLinkValue || '').trim();
        if (!directUrl) {
          message.warning('Veuillez renseigner le lien de destination directe');
          return;
        }
        payload.direct_url = directUrl;
        payload.web_url = directUrl;
        payload.ios_url = directUrl;
        payload.android_url = directUrl;
      } else {
        payload.direct_url = null;
        payload.ios_url = values.ios_url && values.ios_url.trim() !== '' ? values.ios_url.trim() : null;
        payload.android_url = values.android_url && values.android_url.trim() !== '' ? values.android_url.trim() : null;
        payload.web_url = values.web_url && values.web_url.trim() !== '' ? values.web_url.trim() : null;
      }

      setLoading(true);
      await qrCampaignService.updateCampaign(selectedCampaign.id, payload);
      message.success('✅ Campagne QR Code mise à jour !');
      setIsEditModalOpen(false);
      fetchCampaigns(pagination.current, search, sourceFilter, statusFilter);
      fetchStats();
    } catch (err: any) {
      console.error('Erreur mise à jour:', err);
      message.error(err.response?.data?.message || 'Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  // Suppression
  const handleDeleteCampaign = async (id: number) => {
    try {
      setLoading(true);
      await qrCampaignService.deleteCampaign(id);
      message.success('Campagne QR supprimée');
      fetchCampaigns(pagination.current, search, sourceFilter, statusFilter);
      fetchStats();
    } catch (err: any) {
      message.error('Impossible de supprimer la campagne');
    } finally {
      setLoading(false);
    }
  };

  // Toggle activation
  const handleToggleActive = async (camp: QrCampaign, checked: boolean) => {
    try {
      await qrCampaignService.updateCampaign(camp.id, { is_active: checked });
      message.success(checked ? 'Campagne activée' : 'Campagne désactivée');
      setCampaigns(prev => prev.map(c => c.id === camp.id ? { ...c, is_active: checked } : c));
      fetchStats();
    } catch (err) {
      message.error('Erreur changement d\'état');
    }
  };

  // Colonnes du tableau
  const columns = [
    {
      title: 'Aperçu',
      key: 'qr_preview',
      width: 75,
      render: (_: any, record: QrCampaign) => {
        const imageUrl = qrCampaignService.getQrImageUrl(record.id, { format: 'svg' });
        return (
          <Tooltip title="Cliquer pour agrandir et exporter en HD">
            <div
              onClick={() => openPreviewModal(record)}
              style={{
                cursor: 'pointer',
                width: 48,
                height: 48,
                borderRadius: 8,
                border: '1px solid #e5e7eb',
                padding: 2,
                background: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
              }}
            >
              <img
                src={imageUrl}
                alt={record.name}
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            </div>
          </Tooltip>
        );
      }
    },
    {
      title: 'Campagne & Lien Court',
      key: 'name_code',
      render: (_: any, record: QrCampaign) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>
            {record.name}
          </div>
          <Space size={4} style={{ marginTop: 2 }}>
            <Tag color="cyan" style={{ fontFamily: 'monospace', fontSize: 11, margin: 0 }}>
              /q/{record.code}
            </Tag>
            <Tooltip title="Copier le lien court">
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined />}
                onClick={() => copyToClipboard(record.short_url || `${window.location.origin}/q/${record.code}`, 'Lien court')}
              />
            </Tooltip>
          </Space>
          {record.description && (
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
              {record.description}
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Source & Emplacement',
      key: 'source_location',
      width: 220,
      render: (_: any, record: QrCampaign) => {
        const sourceObj = SOURCE_OPTIONS.find(s => s.value === record.source);
        return (
          <div>
            <Tag color="geekblue" style={{ marginBottom: 4 }}>
              {sourceObj ? sourceObj.label : record.source}
            </Tag>
            {record.location ? (
              <div style={{ fontSize: 12, color: '#374151' }}>
                📍 {record.location}
              </div>
            ) : (
              <div style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>
                Non spécifié
              </div>
            )}
          </div>
        );
      }
    },
    {
      title: 'Scans & Appareils',
      key: 'scans',
      width: 190,
      render: (_: any, record: QrCampaign) => (
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#0a543d' }}>
            {record.scans_count.toLocaleString()} scan{record.scans_count > 1 ? 's' : ''}
          </div>
          <Space size={6} direction="horizontal" style={{ marginTop: 3 }}>
            <Tooltip title={`iPhone / iOS : ${record.ios_scans_count} scans`}>
              <Tag icon={<AppleOutlined />} color="black" style={{ fontSize: 11, padding: '0 4px', margin: 0 }}>
                {record.ios_scans_count}
              </Tag>
            </Tooltip>
            <Tooltip title={`Android : ${record.android_scans_count} scans`}>
              <Tag icon={<AndroidOutlined />} color="green" style={{ fontSize: 11, padding: '0 4px', margin: 0 }}>
                {record.android_scans_count}
              </Tag>
            </Tooltip>
            <Tooltip title={`Desktop / Autres : ${record.desktop_scans_count} scans`}>
              <Tag icon={<GlobalOutlined />} color="blue" style={{ fontSize: 11, padding: '0 4px', margin: 0 }}>
                {record.desktop_scans_count}
              </Tag>
            </Tooltip>
          </Space>
        </div>
      )
    },
    {
      title: 'Conversions',
      key: 'conversions',
      width: 120,
      render: (_: any, record: QrCampaign) => {
        const rate = record.scans_count > 0
          ? ((record.conversions_count / record.scans_count) * 100).toFixed(1)
          : '0';
        return (
          <div>
            <div style={{ fontWeight: 600, color: '#1677ff' }}>
              👤 {record.conversions_count} inscrit{record.conversions_count > 1 ? 's' : ''}
            </div>
            <div style={{ fontSize: 11, color: '#6b7280' }}>
              Taux: {rate}%
            </div>
          </div>
        );
      }
    },
    {
      title: 'État',
      key: 'status',
      width: 100,
      render: (_: any, record: QrCampaign) => (
        <Switch
          checked={record.is_active}
          onChange={(checked) => handleToggleActive(record, checked)}
          checkedChildren="Actif"
          unCheckedChildren="Inactif"
          style={{ backgroundColor: record.is_active ? '#0a543d' : undefined }}
        />
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 140,
      render: (_: any, record: QrCampaign) => (
        <Space direction="horizontal" size="small">
          <Tooltip title="Aperçu & Télécharger HD">
            <Button
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => openPreviewModal(record)}
              style={{ background: '#0a543d', borderColor: '#0a543d' }}
            />
          </Tooltip>
          <Tooltip title="Modifier">
            <Button
              type="default"
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEditModal(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Supprimer cette campagne ?"
            description="Le QR code associé cessera de fonctionner."
            onConfirm={() => handleDeleteCampaign(record.id)}
            okText="Oui, supprimer"
            cancelText="Annuler"
            okButtonProps={{ danger: true }}
          >
            <Button
              danger
              type="text"
              size="small"
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      )
    }
  ];

  const hasConfiguredIos = !!storeSettings.iosUrl && storeSettings.iosUrl.trim() !== '';
  const hasConfiguredAndroid = !!storeSettings.androidUrl && storeSettings.androidUrl.trim() !== '';

  return (
    <div style={{ padding: '4px 0 24px 0' }}>
      {/* En-tête de page */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Title level={3} style={{ margin: 0, color: '#0a543d', display: 'flex', alignItems: 'center', gap: 8 }}>
            <QrcodeOutlined /> Campagnes QR Codes & Liens Intelligents
          </Title>
          <Text type="secondary">
            Générez des QR Codes de marque pour vos flyers, affiches et cartes. Suivez les téléchargements et conversions en temps réel.
          </Text>
        </div>
        <Space wrap>
          <Button
            type="primary"
            icon={<LinkOutlined />}
            onClick={openQuickQrModal}
            style={{ background: '#1677ff', borderColor: '#1677ff' }}
          >
            Générer depuis un lien
          </Button>
          <Button
            icon={<SettingOutlined />}
            onClick={() => setIsSettingsModalOpen(true)}
            style={{ borderColor: '#d1d5db' }}
          >
            Configuration des Liens Stores
            {!hasConfiguredIos || !hasConfiguredAndroid ? (
              <Badge status="warning" style={{ marginLeft: 6 }} />
            ) : (
              <Badge status="success" style={{ marginLeft: 6 }} />
            )}
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreateModal}
            style={{ background: '#0a543d', borderColor: '#0a543d' }}
          >
            Nouveau QR Code
          </Button>
        </Space>
      </div>

      {/* Alerte si les liens de stores sont en attente */}
      {(!hasConfiguredIos || !hasConfiguredAndroid) && (
        <Alert
          message="Application en attente de publication sur les Stores"
          description={
            <span>
              Les liens <strong>App Store ({hasConfiguredIos ? '✅ Configuré' : '⏳ Non renseigné'})</strong> et <strong>Google Play Store ({hasConfiguredAndroid ? '✅ Configuré' : '⏳ Non renseigné'})</strong> ne sont pas encore tous définis.
              Tous les QR codes scannés basculent automatiquement et de façon sécurisée vers votre <strong>Landing Page ({storeSettings.webUrl || 'https://smartmaintenance.ci'})</strong>.
              Dès que vous avez vos liens de stores, cliquez sur <em>« Configuration des Liens Stores »</em> ci-dessus.
            </span>
          }
          type="info"
          showIcon
          closable
          style={{ marginBottom: 16, borderRadius: 8 }}
        />
      )}

      {/* Cartes KPI */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={6}>
          <Card loading={statsLoading} bordered={false} style={{ borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <Statistic
              title={<span style={{ color: '#4b5563', fontWeight: 500 }}>Total Campagnes</span>}
              value={stats?.total_campaigns || 0}
              prefix={<QrcodeOutlined style={{ color: '#0a543d' }} />}
              suffix={<span style={{ fontSize: 13, color: '#9ca3af' }}>({stats?.active_campaigns || 0} actives)</span>}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card loading={statsLoading} bordered={false} style={{ borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <Statistic
              title={<span style={{ color: '#4b5563', fontWeight: 500 }}>Total Scans</span>}
              value={stats?.total_scans || 0}
              prefix={<RiseOutlined style={{ color: '#e65100' }} />}
              valueStyle={{ color: '#0a543d', fontWeight: 700 }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card loading={statsLoading} bordered={false} style={{ borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <Statistic
              title={<span style={{ color: '#4b5563', fontWeight: 500 }}>Répartition Mobiles</span>}
              value={stats?.total_ios_scans || 0}
              prefix={<AppleOutlined style={{ color: '#111827' }} />}
              suffix={
                <span style={{ fontSize: 13, color: '#10b981', marginLeft: 8 }}>
                  <AndroidOutlined style={{ marginRight: 2 }} /> {stats?.total_android_scans || 0}
                </span>
              }
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card loading={statsLoading} bordered={false} style={{ borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <Statistic
              title={<span style={{ color: '#4b5563', fontWeight: 500 }}>Inscriptions Converties</span>}
              value={stats?.total_conversions || 0}
              prefix={<CheckCircleOutlined style={{ color: '#1677ff' }} />}
              valueStyle={{ color: '#1677ff', fontWeight: 700 }}
            />
          </Card>
        </Col>
      </Row>

      {/* Bannière Générateur QR Express depuis un Lien */}
      <Card
        size="small"
        style={{
          borderRadius: 12,
          marginBottom: 16,
          background: 'linear-gradient(135deg, #f0fdf4 0%, #eff6ff 100%)',
          borderColor: '#bbf7d0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, padding: '4px 6px' }}>
          <Space align="center" size="middle">
            <div style={{ width: 40, height: 40, borderRadius: 10, background: '#1677ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 20 }}>
              <LinkOutlined />
            </div>
            <div>
              <div style={{ fontWeight: 600, color: '#111827', fontSize: 14 }}>
                Générateur de QR Code Express depuis n'importe quel lien
              </div>
              <div style={{ fontSize: 12, color: '#4b5563' }}>
                Générez et téléchargez instantanément un QR code de marque HD pour n'importe quelle URL (site web, WhatsApp, page promo, réseaux sociaux...).
              </div>
            </div>
          </Space>
          <Button
            type="primary"
            icon={<QrcodeOutlined />}
            onClick={openQuickQrModal}
            style={{ background: '#1677ff', borderColor: '#1677ff', borderRadius: 8, height: 38, fontWeight: 500 }}
          >
            Générer un QR Code
          </Button>
        </div>
      </Card>

      {/* Barre de Filtres & Recherche */}
      <Card bordered={false} style={{ borderRadius: 12, marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={10} md={12}>
            <Input.Search
              placeholder="Rechercher par nom, code, emplacement..."
              allowClear
              enterButton={<SearchOutlined />}
              onSearch={handleSearch}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={12} sm={7} md={6}>
            <Select
              placeholder="Filtrer par source"
              allowClear
              value={sourceFilter}
              onChange={handleSourceChange}
              style={{ width: '100%' }}
            >
              {SOURCE_OPTIONS.map(s => (
                <Option key={s.value} value={s.value}>{s.label}</Option>
              ))}
            </Select>
          </Col>
          <Col xs={12} sm={7} md={6}>
            <Select
              placeholder="Statut"
              allowClear
              value={statusFilter}
              onChange={handleStatusChange}
              style={{ width: '100%' }}
            >
              <Option value="active">🟢 Actifs uniquement</Option>
              <Option value="inactive">⚪ Inactifs uniquement</Option>
            </Select>
          </Col>
        </Row>
      </Card>

      {/* Tableau des Campagnes */}
      <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <Table
          columns={columns}
          dataSource={campaigns}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showTotal: (total, range) => `${range[0]}-${range[1]} sur ${total} campagnes`,
            onChange: (page) => fetchCampaigns(page, search, sourceFilter, statusFilter)
          }}
        />
      </Card>

      {/* MODALE 1 : CRÉATION DE CAMPAGNE QR CODE */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#0a543d' }}>
            <QrcodeOutlined /> Créer une nouvelle Campagne QR Code
          </div>
        }
        open={isCreateModalOpen}
        onOk={handleCreateCampaign}
        onCancel={() => setIsCreateModalOpen(false)}
        okText="Générer le QR Code"
        cancelText="Annuler"
        confirmLoading={loading}
        width={640}
      >
        <Form form={campaignForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="name"
            label="Nom de la campagne"
            rules={[{ required: true, message: 'Veuillez saisir un nom pour cette campagne' }]}
          >
            <Input placeholder="Ex: Flyer Résidence Golf, Affiche Salon BTP, Carte Technicien Yao..." />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="code"
                label={
                  <span>
                    Code court URL <Text type="secondary">(Optionnel)</Text>
                  </span>
                }
                extra="Laissé vide, le code sera généré automatiquement depuis le nom."
              >
                <Input placeholder="Ex: flyer-golf-2026" prefix="/q/" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="source"
                label="Type de support / Source"
                rules={[{ required: true }]}
              >
                <Select placeholder="Sélectionner la source">
                  {SOURCE_OPTIONS.map(s => (
                    <Option key={s.value} value={s.value}>{s.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="location"
            label="Emplacement / Contexte géographique"
          >
            <Input placeholder="Ex: Cocody Riviera Golf, Marcory Zone 4, Bâtiment C..." />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description / Notes internes"
          >
            <TextArea rows={2} placeholder="Précisions sur la diffusion ou les objectifs..." />
          </Form.Item>

          <Divider orientation="left" style={{ fontSize: 13, color: '#6b7280' }}>
            🎨 Style & Personnalisation Visuelle
          </Divider>

          <Row gutter={16} align="middle">
            <Col span={14}>
              <Form.Item
                name="primary_color"
                label="Couleur principale du QR Code"
                rules={[{ required: true }]}
              >
                <Radio.Group>
                  {COLOR_PRESETS.map(c => (
                    <Radio.Button key={c.value} value={c.value} style={{ marginRight: 8, marginBottom: 8 }}>
                      <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', backgroundColor: c.value, marginRight: 6 }} />
                      {c.label}
                    </Radio.Button>
                  ))}
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item
                name="has_logo"
                label="Logo SMART MAINTENANCE"
                valuePropName="checked"
              >
                <Switch checkedChildren="Incruster le Logo" unCheckedChildren="Sans Logo" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" style={{ fontSize: 13, color: '#6b7280' }}>
            🌐 Mode de Redirection de la Campagne
          </Divider>

          <Form.Item label="Type de Redirection" style={{ marginBottom: 14 }}>
            <Radio.Group
              value={redirectionType}
              onChange={(e) => {
                const newType = e.target.value;
                setRedirectionType(newType);
                if (newType === 'direct') {
                  const target = directLinkValue || campaignForm.getFieldValue('direct_url') || campaignForm.getFieldValue('web_url') || '';
                  setDirectLinkValue(target);
                  campaignForm.setFieldsValue({ direct_url: target });
                } else {
                  const currentDirect = directLinkValue || campaignForm.getFieldValue('direct_url');
                  if (currentDirect && !campaignForm.getFieldValue('web_url')) {
                    campaignForm.setFieldsValue({ web_url: currentDirect });
                  }
                }
              }}
            >
              <Radio value="smart">
                <strong>Redirection intelligente</strong> (App Store / Play Store / Web)
              </Radio>
              <Radio value="direct">
                <strong>Lien direct unique</strong> (Redirige tous les appareils vers la même URL)
              </Radio>
            </Radio.Group>
          </Form.Item>

          {redirectionType === 'direct' ? (
            <Form.Item
              name="direct_url"
              label="URL cible directe (tous appareils)"
              extra="Tous les utilisateurs scannant ce QR code (iOS, Android, PC) seront immédiatement dirigés vers cette URL."
              rules={[{ required: true, message: 'Veuillez renseigner le lien de destination directe' }]}
            >
              <Input
                placeholder="Ex: https://www.facebook.com/... ou https://wa.me/... ou https://smartmaintenance.ci"
                prefix={<LinkOutlined />}
                value={directLinkValue}
                onChange={(e) => {
                  const val = e.target.value;
                  setDirectLinkValue(val);
                  campaignForm.setFieldsValue({ direct_url: val });
                }}
                allowClear
              />
            </Form.Item>
          ) : (
            <>
              <Form.Item
                name="ios_url"
                label="Lien App Store iOS spécifique"
              >
                <Input placeholder="Laisser vide pour utiliser le lien App Store par défaut" prefix={<AppleOutlined />} allowClear />
              </Form.Item>

              <Form.Item
                name="android_url"
                label="Lien Google Play Store spécifique"
              >
                <Input placeholder="Laisser vide pour utiliser le lien Play Store par défaut" prefix={<AndroidOutlined />} allowClear />
              </Form.Item>

              <Form.Item
                name="web_url"
                label="Lien Web spécifique de secours"
              >
                <Input placeholder="Laisser vide pour utiliser https://smartmaintenance.ci" prefix={<GlobalOutlined />} allowClear />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>

      {/* MODALE 2 : MODIFICATION DE CAMPAGNE QR CODE */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#0a543d' }}>
            <EditOutlined /> Modifier la Campagne QR Code
          </div>
        }
        open={isEditModalOpen}
        onOk={handleUpdateCampaign}
        onCancel={() => setIsEditModalOpen(false)}
        okText="Enregistrer les modifications"
        cancelText="Annuler"
        confirmLoading={loading}
        width={640}
      >
        <Form form={campaignForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="name"
            label="Nom de la campagne"
            rules={[{ required: true, message: 'Le nom est obligatoire' }]}
          >
            <Input />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="code"
                label="Code court URL (/q/...)"
                rules={[{ required: true, message: 'Le code est obligatoire' }]}
              >
                <Input prefix="/q/" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="source"
                label="Type de support"
                rules={[{ required: true }]}
              >
                <Select>
                  {SOURCE_OPTIONS.map(s => (
                    <Option key={s.value} value={s.value}>{s.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="location"
            label="Emplacement / Contexte géographique"
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description / Notes internes"
          >
            <TextArea rows={2} />
          </Form.Item>

          <Row gutter={16} align="middle">
            <Col span={14}>
              <Form.Item
                name="primary_color"
                label="Couleur principale"
              >
                <Radio.Group>
                  {COLOR_PRESETS.map(c => (
                    <Radio.Button key={c.value} value={c.value} style={{ marginRight: 8, marginBottom: 8 }}>
                      <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', backgroundColor: c.value, marginRight: 6 }} />
                      {c.label}
                    </Radio.Button>
                  ))}
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item
                name="has_logo"
                label="Logo SMART MAINTENANCE"
                valuePropName="checked"
              >
                <Switch checkedChildren="Incrusté" unCheckedChildren="Sans Logo" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" style={{ fontSize: 13, color: '#6b7280' }}>
            🌐 Mode de Redirection
          </Divider>

          <Form.Item label="Type de Redirection" style={{ marginBottom: 14 }}>
            <Radio.Group
              value={redirectionType}
              onChange={(e) => {
                const newType = e.target.value;
                setRedirectionType(newType);
                if (newType === 'direct') {
                  const target = directLinkValue || campaignForm.getFieldValue('direct_url') || campaignForm.getFieldValue('web_url') || '';
                  setDirectLinkValue(target);
                  campaignForm.setFieldsValue({ direct_url: target });
                } else {
                  const currentDirect = directLinkValue || campaignForm.getFieldValue('direct_url');
                  if (currentDirect && !campaignForm.getFieldValue('web_url')) {
                    campaignForm.setFieldsValue({ web_url: currentDirect });
                  }
                }
              }}
            >
              <Radio value="smart">
                <strong>Redirection intelligente</strong> (App Store / Play Store / Web)
              </Radio>
              <Radio value="direct">
                <strong>Lien direct unique</strong> (Redirige tous les appareils vers la même URL)
              </Radio>
            </Radio.Group>
          </Form.Item>

          {redirectionType === 'direct' ? (
            <Form.Item
              name="direct_url"
              label="URL cible directe (tous appareils)"
              extra="Tous les utilisateurs scannant ce QR code (iOS, Android, PC) seront immédiatement dirigés vers cette URL."
              rules={[{ required: true, message: 'Veuillez renseigner le lien de destination directe' }]}
            >
              <Input
                placeholder="Ex: https://www.facebook.com/... ou https://wa.me/... ou https://smartmaintenance.ci"
                prefix={<LinkOutlined />}
                value={directLinkValue}
                onChange={(e) => {
                  const val = e.target.value;
                  setDirectLinkValue(val);
                  campaignForm.setFieldsValue({ direct_url: val });
                }}
                allowClear
              />
            </Form.Item>
          ) : (
            <>
              <Form.Item name="ios_url" label="Lien App Store iOS spécifique">
                <Input prefix={<AppleOutlined />} placeholder="Laisser vide pour hériter du lien global" allowClear />
              </Form.Item>

              <Form.Item name="android_url" label="Lien Google Play Store spécifique">
                <Input prefix={<AndroidOutlined />} placeholder="Laisser vide pour hériter du lien global" allowClear />
              </Form.Item>

              <Form.Item name="web_url" label="Lien Web spécifique">
                <Input prefix={<GlobalOutlined />} placeholder="Laisser vide pour https://smartmaintenance.ci" allowClear />
              </Form.Item>
            </>
          )}

          <Form.Item name="is_active" label="Statut de la campagne" valuePropName="checked">
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>
        </Form>
      </Modal>

      {/* MODALE 3 : APERÇU GRAND FORMAT & TÉLÉCHARGEMENT HD */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#0a543d' }}>
            <QrcodeOutlined /> Aperçu & Téléchargement HD du QR Code
          </div>
        }
        open={isPreviewModalOpen}
        onCancel={() => setIsPreviewModalOpen(false)}
        footer={[
          <Button key="close" onClick={() => setIsPreviewModalOpen(false)}>
            Fermer
          </Button>
        ]}
        width={580}
      >
        {selectedCampaign && (
          <div style={{ textAlign: 'center', padding: '12px 0' }}>
            <Title level={4} style={{ margin: '0 0 4px 0', color: '#111827' }}>
              {selectedCampaign.name}
            </Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
              {selectedCampaign.location ? `📍 ${selectedCampaign.location} — ` : ''}
              {SOURCE_OPTIONS.find(s => s.value === selectedCampaign.source)?.label || selectedCampaign.source}
            </Text>

            {/* Cadre d'affichage du QR Code avec fond blanc propre pour impression */}
            <div
              style={{
                display: 'inline-block',
                padding: 24,
                borderRadius: 16,
                background: '#ffffff',
                border: '2px solid #e5e7eb',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                marginBottom: 20
              }}
            >
              <img
                src={qrCampaignService.getQrImageUrl(selectedCampaign.id, { format: 'svg' })}
                alt={selectedCampaign.name}
                style={{ width: 240, height: 240, objectFit: 'contain' }}
              />
              <div style={{ marginTop: 8, fontSize: 13, fontWeight: 700, color: '#0a543d' }}>
                SMART MAINTENANCE by MCT
              </div>
              <div style={{ fontSize: 11, color: '#6b7280' }}>
                Scannez pour télécharger l'application
              </div>
            </div>

            {/* Lien court */}
            <div style={{ background: '#f3f4f6', padding: '10px 16px', borderRadius: 8, marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontFamily: 'monospace', fontSize: 13, color: '#1f2937' }}>
                {selectedCampaign.short_url || `${window.location.origin}/q/${selectedCampaign.code}`}
              </span>
              <Button
                type="primary"
                size="small"
                icon={<CopyOutlined />}
                onClick={() => copyToClipboard(selectedCampaign.short_url || `${window.location.origin}/q/${selectedCampaign.code}`, 'Lien court')}
                style={{ background: '#0a543d', borderColor: '#0a543d' }}
              >
                Copier
              </Button>
            </div>

            {/* Boutons de téléchargement HD */}
            <Space size="middle" wrap style={{ justifyContent: 'center', marginBottom: 16 }}>
              <Button
                type="primary"
                size="large"
                icon={<DownloadOutlined />}
                loading={downloadingFormat === 'png'}
                onClick={() => handleDownloadImage('png')}
                style={{ background: '#0a543d', borderColor: '#0a543d', height: 44, borderRadius: 8 }}
              >
                Télécharger PNG HD (1024px)
              </Button>
              <Button
                size="large"
                icon={<DownloadOutlined />}
                loading={downloadingFormat === 'svg'}
                onClick={() => handleDownloadImage('svg')}
                style={{ height: 44, borderRadius: 8, borderColor: '#0a543d', color: '#0a543d' }}
              >
                Télécharger SVG Vectoriel
              </Button>
            </Space>

            <div style={{ fontSize: 12, color: '#6b7280' }}>
              💡 <strong>Pour l'imprimeur</strong> : Le format <strong>PNG HD</strong> convient parfaitement pour les flyers et affiches A4/A3. Le format <strong>SVG</strong> est recommandé pour les bannières géantes.
            </div>
          </div>
        )}
      </Modal>

      {/* MODALE 4 : CONFIGURATION GLOBALE DES LIENS DES STORES */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#0a543d' }}>
            <SettingOutlined /> Configuration Globale des Liens Stores & Landing Page
          </div>
        }
        open={isSettingsModalOpen}
        onOk={handleSaveStoreSettings}
        onCancel={() => setIsSettingsModalOpen(false)}
        okText="Enregistrer les liens"
        cancelText="Annuler"
        confirmLoading={settingsLoading}
        width={620}
      >
        <Paragraph type="secondary" style={{ marginTop: 12 }}>
          Ces liens sont utilisés par tous les QR Codes du système. Si vous n'avez pas encore publié votre application sur l'App Store ou Google Play, laissez ces champs vides : <strong>les utilisateurs seront automatiquement redirigés vers la Landing Page Web sans aucune erreur</strong>.
        </Paragraph>

        <Form form={settingsForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="iosUrl"
            label={
              <Space>
                <AppleOutlined /> <span>Lien Apple App Store (iOS / iPhone)</span>
                {hasConfiguredIos ? <Tag color="success">Configuré</Tag> : <Tag color="default">En attente de publication</Tag>}
              </Space>
            }
          >
            <Input
              placeholder="Ex: https://apps.apple.com/app/smart-maintenance/id6741496734"
              allowClear
            />
          </Form.Item>

          <Form.Item
            name="androidUrl"
            label={
              <Space>
                <AndroidOutlined /> <span>Lien Google Play Store (Android)</span>
                {hasConfiguredAndroid ? <Tag color="success">Configuré</Tag> : <Tag color="default">En attente de publication</Tag>}
              </Space>
            }
          >
            <Input
              placeholder="Ex: https://play.google.com/store/apps/details?id=ci.mct.maintenance"
              allowClear
            />
          </Form.Item>

          <Form.Item
            name="webUrl"
            label={
              <Space>
                <GlobalOutlined /> <span>Lien Landing Page Web (Fallback universel)</span>
                <Tag color="processing">Recommandé</Tag>
              </Space>
            }
            rules={[{ required: true, message: 'Le lien web de secours est requis' }]}
          >
            <Input
              placeholder="Ex: https://smartmaintenance.ci"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* MODALE 5 : GÉNÉRATEUR RAPIDE DE QR CODE DEPUIS UN LIEN */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#1677ff' }}>
            <LinkOutlined style={{ fontSize: 20 }} />
            <span style={{ fontWeight: 600 }}>Générateur de QR Code Instantané depuis un Lien</span>
          </div>
        }
        open={isQuickQrModalOpen}
        onCancel={() => setIsQuickQrModalOpen(false)}
        footer={null}
        width={860}
        destroyOnClose={false}
      >
        <div style={{ marginTop: 12 }}>
          <Row gutter={[24, 24]}>
            {/* Colonne Gauche : Paramètres & Personnalisation */}
            <Col xs={24} md={13}>
              <div style={{ marginBottom: 18 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#374151', marginBottom: 6 }}>
                  🔗 Lien ou URL à encoder <span style={{ color: '#ef4444' }}>*</span>
                </div>
                <Input
                  size="large"
                  placeholder="Ex: https://smartmaintenance.ci ou https://wa.me/..."
                  prefix={<GlobalOutlined style={{ color: '#1677ff' }} />}
                  value={customUrl}
                  onChange={(e) => setCustomUrl(e.target.value)}
                  allowClear
                  style={{ borderRadius: 8 }}
                />

                {/* Suggestions / Raccourcis rapides */}
                <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                  <Text type="secondary" style={{ fontSize: 11, marginRight: 2 }}>Suggestions :</Text>
                  {QUICK_LINK_PRESETS.map((preset) => (
                    <Tag
                      key={preset.label}
                      color="blue"
                      style={{ cursor: 'pointer', borderRadius: 12, fontSize: 11, padding: '1px 8px' }}
                      onClick={() => setCustomUrl(preset.url)}
                    >
                      {preset.label}
                    </Tag>
                  ))}
                  {customUrl && (
                    <Tag
                      color="default"
                      style={{ cursor: 'pointer', borderRadius: 12, fontSize: 11, padding: '1px 8px' }}
                      onClick={() => setCustomUrl('')}
                    >
                      Effacer
                    </Tag>
                  )}
                </div>
                {customUrl && !/^https?:\/\//i.test(customUrl) && !/^[a-z0-9+.-]+:/i.test(customUrl) && (
                  <div style={{ fontSize: 11, color: '#f59e0b', marginTop: 4 }}>
                    ⚠️ Ce lien ne commence pas par https:// (sera interprété comme texte ou lien relatif).
                    <Button
                      type="link"
                      size="small"
                      style={{ padding: '0 4px', height: 'auto', fontSize: 11 }}
                      onClick={() => setCustomUrl(`https://${customUrl.trim()}`)}
                    >
                      Ajouter https://
                    </Button>
                  </div>
                )}
              </div>

              <div style={{ marginBottom: 18 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#374151', marginBottom: 6 }}>
                  📝 Titre ou annotation sous le QR Code <Text type="secondary" style={{ fontWeight: 400 }}>(Optionnel)</Text>
                </div>
                <Input
                  placeholder="Ex: Scannez pour commander, Offre Spéciale -20%..."
                  value={customTitle}
                  onChange={(e) => setCustomTitle(e.target.value)}
                  allowClear
                  style={{ borderRadius: 8 }}
                />
              </div>

              <Divider orientation="left" style={{ fontSize: 12, color: '#6b7280', margin: '14px 0' }}>
                🎨 Style & Résilience
              </Divider>

              {/* Couleur principale */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#374151', marginBottom: 6 }}>
                  Couleur du QR Code
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                  {COLOR_PRESETS.map((c) => (
                    <Button
                      key={c.value}
                      size="small"
                      onClick={() => setCustomColor(c.value)}
                      style={{
                        borderRadius: 6,
                        borderColor: customColor === c.value ? c.value : '#d1d5db',
                        borderWidth: customColor === c.value ? 2 : 1,
                        background: customColor === c.value ? `${c.value}15` : '#fff',
                        fontWeight: customColor === c.value ? 600 : 400
                      }}
                    >
                      <span
                        style={{
                          display: 'inline-block',
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          backgroundColor: c.value,
                          marginRight: 6
                        }}
                      />
                      {c.label.split(' ')[0]}
                    </Button>
                  ))}
                  <ColorPicker
                    value={customColor}
                    onChange={(color) => setCustomColor(typeof color === 'string' ? color : color.toHexString())}
                    showText
                    size="small"
                  />
                </div>
              </div>

              {/* Options logo & niveau d'erreur */}
              <Row gutter={16} align="middle">
                <Col span={12}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: '#374151', marginBottom: 4 }}>
                    Logo SMART MAINTENANCE
                  </div>
                  <Switch
                    checked={hasCustomLogo}
                    onChange={(checked) => setHasCustomLogo(checked)}
                    checkedChildren="Incrusté"
                    unCheckedChildren="Sans Logo"
                  />
                </Col>
                <Col span={12}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: '#374151', marginBottom: 4 }}>
                    Correction d'erreur
                  </div>
                  <Select
                    value={customLevel}
                    onChange={(val) => setCustomLevel(val)}
                    style={{ width: '100%' }}
                    size="small"
                  >
                    <Option value="H">H (30% - Idéal avec logo)</Option>
                    <Option value="Q">Q (25%)</Option>
                    <Option value="M">M (15%)</Option>
                    <Option value="L">L (7%)</Option>
                  </Select>
                </Col>
              </Row>

              <div style={{ marginTop: 20, padding: 12, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: 12, color: '#475569', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <span style={{ fontSize: 16 }}>💡</span>
                  <div>
                    <strong>Astuce :</strong> Ce générateur fonctionne en direct. Vous pouvez télécharger votre QR code immédiatement, ou bien l'enregistrer comme <em>Campagne de suivi</em> pour comptabiliser les scans et les statistiques en temps réel.
                  </div>
                </div>
              </div>
            </Col>

            {/* Colonne Droite : Prévisualisation & Actions d'Export */}
            <Col xs={24} md={11}>
              <div
                style={{
                  background: '#f9fafb',
                  borderRadius: 12,
                  padding: 20,
                  textAlign: 'center',
                  border: '1px solid #e5e7eb',
                  boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.04)'
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12 }}>
                  Aperçu en direct
                </div>

                {/* Cadre du QR Code */}
                <div
                  style={{
                    display: 'inline-block',
                    background: '#ffffff',
                    padding: 16,
                    borderRadius: 16,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    border: '1px solid #e5e7eb',
                    marginBottom: 12
                  }}
                >
                  <div id="custom-quick-qrcode">
                    <QRCode
                      value={customUrl.trim() || 'https://smartmaintenance.ci'}
                      size={210}
                      color={customColor}
                      icon={hasCustomLogo ? '/logo_smart_mct.png' : undefined}
                      iconSize={52}
                      errorLevel={customLevel}
                      bordered={false}
                    />
                  </div>

                  {/* Version SVG cachée pour l'export vectoriel */}
                  <div id="custom-quick-qrcode-svg" style={{ display: 'none' }}>
                    <QRCode
                      value={customUrl.trim() || 'https://smartmaintenance.ci'}
                      type="svg"
                      size={512}
                      color={customColor}
                      icon={hasCustomLogo ? '/logo_smart_mct.png' : undefined}
                      iconSize={120}
                      errorLevel={customLevel}
                    />
                  </div>

                  {customTitle && (
                    <div style={{ marginTop: 8, fontWeight: 600, fontSize: 13, color: customColor, maxWidth: 220, wordBreak: 'break-word' }}>
                      {customTitle}
                    </div>
                  )}
                  <div style={{ marginTop: 4, fontSize: 11, fontWeight: 700, color: '#0a543d' }}>
                    SMART MAINTENANCE by MCT
                  </div>
                </div>

                {/* URL preview */}
                <div
                  style={{
                    background: '#ffffff',
                    padding: '8px 12px',
                    borderRadius: 6,
                    border: '1px solid #e5e7eb',
                    marginBottom: 16,
                    fontSize: 12,
                    color: '#374151',
                    fontFamily: 'monospace',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                  title={customUrl}
                >
                  {customUrl.trim() || 'https://smartmaintenance.ci'}
                </div>

                {/* Boutons d'exportation */}
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={downloadCustomPng}
                    block
                    style={{ background: '#0a543d', borderColor: '#0a543d', height: 40, borderRadius: 8, fontWeight: 500 }}
                  >
                    Télécharger PNG HD (1024px)
                  </Button>
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={downloadCustomSvg}
                    block
                    style={{ height: 38, borderRadius: 8, borderColor: '#0a543d', color: '#0a543d' }}
                  >
                    Télécharger SVG Vectoriel
                  </Button>
                  <Button
                    icon={<CopyOutlined />}
                    onClick={copyCustomUrl}
                    block
                    style={{ height: 36, borderRadius: 8 }}
                  >
                    Copier le Lien
                  </Button>
                </Space>

                {/* Passerelle vers la création de campagne avec tracking */}
                <div
                  style={{
                    marginTop: 16,
                    padding: '10px 12px',
                    background: '#f0fdf4',
                    borderRadius: 8,
                    border: '1px solid #bbf7d0',
                    textAlign: 'left'
                  }}
                >
                  <div style={{ fontSize: 11, color: '#166534', marginBottom: 8, lineHeight: 1.4 }}>
                    📈 <strong>Suivre les scans ?</strong> Enregistrez ce QR code comme campagne pour mesurer les statistiques et les conversions en temps réel.
                  </div>
                  <Button
                    size="small"
                    type="primary"
                    icon={<RiseOutlined />}
                    onClick={convertCustomToCampaign}
                    block
                    style={{ background: '#16a34a', borderColor: '#16a34a', borderRadius: 6, fontWeight: 500 }}
                  >
                    Enregistrer comme Campagne Suivie
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </Modal>
    </div>
  );
};

export default QRCodesPage;
